import { useId, useState, type FormEvent } from 'react';
import { useSWRConfig } from 'swr';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { EmojiPicker } from '../../../shared/ui/EmojiPicker.js';
import {
  DEFAULT_CATEGORY_EMOJI,
  resolveCategoryEmoji,
} from '../../../shared/ui/icons/categoryEmoji.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { toggleFavorite, updatePreferences } from '../../preferences/data/preferencesRepository.js';
import { PREFERENCES_KEY, usePreferences } from '../../preferences/hooks/usePreferences.js';
import {
  applyCategoryOrder,
  createCategory,
  updateCategory,
  type Category,
} from '../data/categoriesRepository.js';
import { useCategories } from '../hooks/useCategories.js';
import { duplicateCategoryMessage, findDuplicateCategory } from '../model/duplicateName.js';
import styles from './CategoriesPage.module.css';

/**
 * "Danh mục" — the spending categories: rename, reorder, hide, mark favourites (plan step 1.1).
 *
 * SHAPE (PO decision N10, 2026-08-19). Three fixed parents come from the seed and are shown
 * here as group headings, not as rows: the user cannot create, rename or hide a parent, only
 * put categories inside one. Every category they do create is a child, so the report always
 * has something to group by.
 *
 * There is no income tab. Income runs on "Nguồn thu" (`income_sources`), which also carries
 * the stability class the report needs (B10); a second, parallel list of income categories
 * existed here and was read by nothing — creating one led straight to a category Quick Add
 * would never show.
 *
 * The order matters because Quick Add shows favourites first and then this order (step 1.2):
 * the fewer taps between opening the app and saving an expense, the more likely the family
 * keeps using it at all.
 */
export function CategoriesPage() {
  const contextState = useCurrentContext();
  const categoriesState = useCategories();
  const [showHidden, setShowHidden] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (contextState.status === 'loading' || categoriesState.status === 'loading') {
    return <LoadingState label="Đang tải danh mục…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (categoriesState.status === 'error') {
    return <ErrorState message={categoriesState.message} onRetry={categoriesState.retry} />;
  }

  const all = categoriesState.categories.filter((category) => category.categoryType === 'expense');
  const visible = all.filter((category) => showHidden || category.isActive);
  // Parents are read from `all`, never from `visible`: hiding is a thing that happens to
  // categories, and a hidden parent would take its visible children off the screen with it.
  const parents = all.filter((category) => category.parentId === null);
  const hiddenCount = all.filter(
    (category) => !category.isActive && category.parentId !== null,
  ).length;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Danh mục</h1>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}

      {/* One card per parent: the heading IS the parent, so it needs no row of its own and
          cannot be edited away. */}
      {parents.map((parent) => (
        <section key={parent.id} className={lists.card}>
          <h2 className={lists.cardTitle}>
            <span className={styles.parentIcon} aria-hidden="true">
              {resolveCategoryEmoji(parent)}
            </span>
            {parent.name}
          </h2>
          <ChildList
            parent={parent}
            categories={visible}
            allCategories={categoriesState.categories}
            onChanged={() => void categoriesState.refresh()}
          />
        </section>
      ))}

      {hiddenCount > 0 ? (
        <label className={lists.toggle}>
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(event) => setShowHidden(event.target.checked)}
          />
          Hiện {hiddenCount} danh mục đã ẩn
        </label>
      ) : null}

      <section className={lists.card}>
        <h2 className={lists.cardTitle}>Thêm danh mục</h2>
        <CategoryForm
          householdId={contextState.context.household.id}
          parents={parents}
          allCategories={categoriesState.categories}
          onCreated={() => {
            setNotice('Đã thêm danh mục.');
            void categoriesState.refresh();
          }}
        />
      </section>
    </div>
  );
}

function ChildList({
  parent,
  categories,
  allCategories,
  onChanged,
}: {
  parent: Category;
  categories: readonly Category[];
  allCategories: readonly Category[];
  onChanged: () => void;
}) {
  const children = categories.filter((category) => category.parentId === parent.id);
  if (children.length === 0) {
    return <p className={forms.hint}>Chưa có danh mục nào trong nhóm này.</p>;
  }

  return (
    <ul className={lists.list}>
      {children.map((child, index) => (
        <li key={child.id}>
          <CategoryRow
            category={child}
            siblings={children}
            allCategories={allCategories}
            index={index}
            onChanged={onChanged}
          />
        </li>
      ))}
    </ul>
  );
}

function CategoryRow({
  category,
  siblings,
  allCategories,
  index,
  onChanged,
}: {
  category: Category;
  siblings: readonly Category[];
  /** Every category of the household — what the duplicate-name rule is checked against. */
  allCategories: readonly Category[];
  index: number;
  onChanged: () => void;
}) {
  const { mutate } = useSWRConfig();
  const preferencesState = usePreferences();
  const nameId = useId();

  const [isEditing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [emoji, setEmoji] = useState(() => resolveCategoryEmoji(category));
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      onChanged();
      await mutate(PREFERENCES_KEY);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Moves the row one place within its own sibling group — children never cross parents. */
  function move(direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= siblings.length) return;

    const reordered = [...siblings];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved!);
    void run(() =>
      applyCategoryOrder(reordered.map((item) => ({ id: item.id, sortOrder: item.sortOrder }))),
    );
  }

  if (isEditing) {
    const trimmed = name.trim();
    const unchanged = trimmed === category.name && emoji === resolveCategoryEmoji(category);
    // Renaming onto a name already in use is refused too — same confusion, same rule.
    const duplicate = findDuplicateCategory(allCategories, {
      name,
      categoryType: category.categoryType,
      householdId: category.householdId,
      excludeId: category.id,
    });

    return (
      <div className={styles.editRow}>
        <div className={forms.field}>
          <label htmlFor={nameId}>Tên danh mục</label>
          <input
            id={nameId}
            className={forms.input}
            type="text"
            value={name}
            autoComplete="off"
            enterKeyHint="done"
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className={forms.field}>
          <span>Biểu tượng</span>
          <EmojiPicker value={emoji} onChange={setEmoji} />
        </div>

        <div className={forms.actions}>
          <button
            type="button"
            className={forms.secondary}
            onClick={() => {
              setName(category.name);
              setEmoji(resolveCategoryEmoji(category));
              setEditing(false);
            }}
          >
            Hủy
          </button>
          <button
            type="button"
            className={forms.primary}
            disabled={busy || trimmed === '' || unchanged || duplicate !== null}
            onClick={() =>
              void run(() => updateCategory(category.id, { name, icon: emoji })).then(() =>
                setEditing(false),
              )
            }
          >
            Lưu
          </button>
        </div>

        {duplicate ? (
          <p className={forms.fieldError} role="alert">
            {duplicateCategoryMessage(duplicate)}
          </p>
        ) : null}

        {/* Hiding lives in the editor, not on the row: it is rare, and putting it beside the
            reorder arrows is how a tidy-up turns into an accidental hidden category. */}
        <button
          type="button"
          className={cx(styles.editHide, category.isActive && styles.editHideDanger)}
          disabled={busy}
          onClick={() =>
            void run(() => updateCategory(category.id, { isActive: !category.isActive }))
          }
        >
          {category.isActive ? 'Ẩn danh mục này' : 'Hiện lại danh mục này'}
        </button>

        {message ? (
          <p className={forms.error} role="alert">
            {message}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className={styles.row}>
        {/* The whole row opens the editor: a 44px-tall target beats hunting for a pencil. */}
        <button
          type="button"
          className={styles.rowMain}
          onClick={() => setEditing(true)}
          aria-label={`Sửa ${category.name}`}
        >
          <span className={styles.rowIcon} aria-hidden="true">
            {resolveCategoryEmoji(category)}
          </span>
          <span className={styles.rowName}>{category.name}</span>
          {!category.isActive ? <span className={styles.rowBadge}>Đã ẩn</span> : null}
          <span className={lists.chevron} aria-hidden="true">
            ›
          </span>
        </button>

        {/* Borderless icon buttons, still 44px to the finger: the bordered, labelled row of
            five was heavy enough to bury the category names (PO, 2026-08-19). */}
        <span className={styles.rowActions}>
          {preferencesState.status === 'ready' ? (
            <button
              type="button"
              className={cx(styles.iconButton, category.isFavorite && styles.iconButtonActive)}
              aria-pressed={category.isFavorite}
              aria-label={
                category.isFavorite
                  ? `Bỏ yêu thích ${category.name}`
                  : `Đánh dấu yêu thích ${category.name}`
              }
              disabled={busy}
              onClick={() =>
                void run(() =>
                  updatePreferences({
                    favoriteCategoryIds: toggleFavorite(
                      preferencesState.preferences.favoriteCategoryIds,
                      category.id,
                    ),
                  }),
                )
              }
            >
              {category.isFavorite ? '★' : '☆'}
            </button>
          ) : null}

          <button
            type="button"
            className={styles.iconButton}
            aria-label={`Chuyển ${category.name} lên trên`}
            disabled={busy || index === 0}
            onClick={() => move(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className={styles.iconButton}
            aria-label={`Chuyển ${category.name} xuống dưới`}
            disabled={busy || index === siblings.length - 1}
            onClick={() => move(1)}
          >
            ↓
          </button>
        </span>
      </div>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}
    </>
  );
}

/**
 * Creates a CHILD category, always. The parent is required and the list of parents is fixed
 * by the seed — there is no "make this a top-level category" option, because a category with
 * no parent is a category the report cannot group (PO decision N10).
 */
function CategoryForm({
  householdId,
  parents,
  allCategories,
  onCreated,
}: {
  householdId: string;
  parents: readonly Category[];
  allCategories: readonly Category[];
  onCreated: () => void;
}) {
  const nameId = useId();
  const parentId = useId();

  const [name, setName] = useState('');
  // Pre-selected rather than blank: "Chi tiêu sinh hoạt" is where an ad-hoc category almost
  // always belongs, and a required field the user must open anyway is a required field the
  // fast path should answer for them.
  const [parent, setParent] = useState(() => parents[0]?.id ?? '');
  const [emoji, setEmoji] = useState<string>(DEFAULT_CATEGORY_EMOJI);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  const duplicate = findDuplicateCategory(allCategories, {
    name,
    categoryType: 'expense',
    householdId,
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim() === '' || parent === '' || duplicate !== null) {
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await createCategory(householdId, {
        name,
        categoryType: 'expense',
        icon: emoji,
        parentId: parent,
      });
      setName('');
      setEmoji(DEFAULT_CATEGORY_EMOJI);
      onCreated();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={forms.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
      <div className={forms.field}>
        <label htmlFor={nameId}>Tên danh mục</label>
        <input
          id={nameId}
          className={forms.input}
          type="text"
          value={name}
          placeholder="Ví dụ: Cà phê"
          autoComplete="off"
          enterKeyHint="done"
          aria-invalid={duplicate !== null}
          onChange={(event) => setName(event.target.value)}
        />
        {duplicate ? (
          <p className={forms.fieldError} role="alert">
            {duplicateCategoryMessage(duplicate)}
          </p>
        ) : null}
      </div>

      <div className={forms.field}>
        <label htmlFor={parentId}>Thuộc nhóm</label>
        <select
          id={parentId}
          className={forms.input}
          value={parent}
          required
          onChange={(event) => setParent(event.target.value)}
        >
          {parents.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
        <p className={forms.hint}>
          Mỗi danh mục thuộc về một nhóm, để báo cáo gom lại được — ví dụ “Chi tiêu sinh hoạt → Cà
          phê”.
        </p>
      </div>

      <div className={forms.field}>
        <span>Biểu tượng</span>
        <EmojiPicker value={emoji} onChange={setEmoji} />
      </div>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <button
        type="submit"
        className={forms.primary}
        disabled={isSaving || name.trim() === '' || duplicate !== null}
      >
        {isSaving ? 'Đang lưu…' : 'Thêm danh mục'}
      </button>
    </form>
  );
}
